import { BackendError, Service } from '@genial/services';
import {
  FinanceEnumsBrokerageFilterTypeDateEnum,
  FinanceEnumsBrokerageInvoiceTypeEnum,
  ResponsesBrokerageInvoiceItemResponse,
} from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { screenName } from '../../constants';
import {
  reportComplementaryTitleOptionsState,
  reportFilterParamsState,
  reportMarketOptionState,
  reportMonthSelectedOptionsState,
  reportPeriodDatesState,
  reportPeriodOptionState,
  reportPeriodYearOptionState,
  reportTypeOptionState,
  reportYearBrokerageOptionState,
  reportYearOptionState,
} from '../atom';
import { reportsMonths } from '../constants';
import { ReportFitlerParams, ReportPeriodDates } from '../types';

export const updateReportComplementaryTitleOptionSelector = selector({
  key: 'updateReportComplementaryTitleOptionSelector',
  set: ({ set }, value) => {
    set(reportComplementaryTitleOptionsState, value as string);
  },
  get: ({ get }) => {
    const params = get(reportComplementaryTitleOptionsState);
    return params;
  },
});

export const updateReportTypeOptionSelector = selector({
  key: 'updateReportTypeOptionSelector',
  set: ({ set }, value) => {
    set(reportTypeOptionState, value as string);
  },
  get: ({ get }) => {
    const params = get(reportTypeOptionState);
    return params;
  },
});

export const updateReportMarketOptionSelector = selector({
  key: 'updateReportMarketOptionSelector',
  set: ({ set }, value) => {
    set(reportMarketOptionState, value as string);
  },
  get: ({ get }) => {
    const params = get(reportMarketOptionState);
    return params;
  },
});

export const updateReportPeriodOptionSelector = selector({
  key: 'updateReportPeriodOptionSelector',
  set: ({ set }, value) => {
    set(reportPeriodOptionState, value as string);
  },
  get: ({ get }) => {
    const params = get(reportPeriodOptionState);
    return params;
  },
});

export const updateReportPeriodDatesSelector = selector({
  key: 'updateReportPeriodDatesSelector',
  set: ({ set }, value) => {
    const newValue = value as ReportPeriodDates;
    set(reportPeriodDatesState, newValue);
  },
  get: ({ get }) => {
    const params = get(reportPeriodDatesState);
    return params;
  },
});

export const reportPeriodIsDailySelector = selector({
  key: 'reportPeriodIsDailySelector',
  get: ({ get }) => {
    const currentReportPeriod = get(reportPeriodOptionState);
    return currentReportPeriod === 'Daily';
  },
});

export const updateReportYearOptionSelector = selector({
  key: 'updateReportYearOptionSelector',
  set: ({ set }, value) => {
    set(reportYearOptionState, value as string);
  },
  get: ({ get }) => {
    const params = get(reportYearOptionState);
    return params;
  },
});

export const updateReportPeriodYearOptionSelector = selector({
  key: 'updateReportPeriodYearOptionSelector',
  set: ({ set }, value) => {
    set(reportPeriodYearOptionState, value as string);
  },
  get: ({ get }) => {
    const params = get(reportPeriodYearOptionState);
    return params;
  },
});

export const updateReportYearBrokerageOptionSelector = selector({
  key: 'updateReportYearBrokerageOptionSelector',
  set: ({ set }, value) => {
    set(reportYearBrokerageOptionState, value as string);
  },
  get: ({ get }) => {
    const params = get(reportYearBrokerageOptionState);
    return params;
  },
});

export const updateReportMonthSelectedOptionsSelector = selector({
  key: 'updateReportMonthSelectedOptionsSelector',
  set: ({ set, get }, value) => {
    const newValue = value as string;
    const currentReportMonthsSelectedOptions = get(reportMonthSelectedOptionsState);

    if (currentReportMonthsSelectedOptions.includes(newValue)) {
      const reportsMonthSelectedOptionsFiltered = currentReportMonthsSelectedOptions.filter(
        (item) => item !== newValue
      );
      set(reportMonthSelectedOptionsState, reportsMonthSelectedOptionsFiltered);
    } else {
      const reportsMonthSelectedOptionsUpdatedData = [
        ...currentReportMonthsSelectedOptions,
        newValue,
      ];
      set(reportMonthSelectedOptionsState, reportsMonthSelectedOptionsUpdatedData);
    }
  },
  get: ({ get }) => {
    const params = get(reportMonthSelectedOptionsState);
    return params;
  },
});

export const updateReportFilterParamsSelector = selector({
  key: 'updateReportFilterParamsSelector',
  set: ({ set, get }, value) => {
    const currentReportFilterParams = get(reportFilterParamsState);
    const newValue = value as Partial<ReportFitlerParams>;
    set(reportFilterParamsState, { ...currentReportFilterParams, ...newValue });
  },
  get: ({ get }) => {
    const params = get(reportFilterParamsState);
    return params;
  },
});

export const incomeTaxBaseYearReportsQuery = selector({
  key: 'incomeTaxBaseYearReportsQuery',
  get: async () => {
    try {
      const { data } = await Service.Finance.V1.getIncomeTaxBaseYear({
        screenName,
      });

      const normalizedData =
        data?.map((item) => ({
          label: String(item.year || 'Error'),
          value: String(item.year || 'Error'),
        })) || [];

      return normalizedData;
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return [];
      }

      throw error;
    }
  },
});

export const incomeTaxTypeReportsQuery = selector({
  key: 'incomeTaxTypeReportsQuery',
  get: async () => {
    try {
      const { data } = await Service.Finance.V1.getIncomeTaxReportType({
        screenName,
      });

      const normalizedData =
        Object.keys(data?.reportTypes!).map((key) => ({
          label: data?.reportTypes![key] || 'Erro',
          value: key,
        })) || [];

      return normalizedData;
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return [];
      }

      throw error;
    }
  },
});

export const brokerageInvoicesQuery = selectorFamily<
  ResponsesBrokerageInvoiceItemResponse[] | null | undefined,
  {
    accountNumber: number;
  }
>({
  key: 'brokerageInvoicesQuery',
  get: ({ accountNumber }) => {
    return async ({ get }) => {
      try {
        const filterType = get(reportPeriodOptionState) as FinanceEnumsBrokerageFilterTypeDateEnum;
        const brokerageInvoiceParams = get(reportFilterParamsState);

        const brokerageInvoiceDailyParams = {
          startDate: brokerageInvoiceParams.reportPeriodDates.startDate,
          endDate: brokerageInvoiceParams.reportPeriodDates.endDate,
          accountNumber,
          screenName,
        };

        const brokerageInvoiceMonthParams = {
          invoiceType:
            brokerageInvoiceParams.reportMarketOption as FinanceEnumsBrokerageInvoiceTypeEnum,
          accountNumber,
          screenName,
          year: Number(brokerageInvoiceParams.reportYearOption),
          ...(brokerageInvoiceParams.reportMonthSelectedOptions.length > 0 && {
            months: brokerageInvoiceParams.reportMonthSelectedOptions.map(
              (month) => reportsMonths.items.find((item) => item.value === month)!.filterValue
            ),
          }),
        };

        const brokerageInvoiceTypeParams =
          filterType === 'Daily' ? brokerageInvoiceDailyParams : brokerageInvoiceMonthParams;

        const { data } = await Service.Finance.V1.getBrokerageByInvoicesAccountNumber({
          ...brokerageInvoiceTypeParams,
          accountNumber,
          screenName,
          filterType,
        });

        const response = data?.map((item) => item?.brokerageInvoice) || [];
        return response;
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return [];
        }

        throw error;
      }
    };
  },
});
